import { AsYouType, parsePhoneNumberFromString as parsePhoneNumber } from "libphonenumber-js"
import { useEffect, useMemo, useState } from "react"
import countries from "react-intl-tel-input/dist/components/AllCountries"
import "react-intl-tel-input/dist/main.css"
import { IOption } from "src/types"
import { Selector } from "../select"
import "./phone-input-style.scss"

interface ICountryMeta {
  iso2: string
  dialCode: string
  priority: number
}

type CountryPhone = IOption<string, ICountryMeta>
const getFormattedPhone = (input: string) => {
  try {
    const parsedData = parsePhoneNumber(input?.trimEnd() ?? "")
    if (parsedData == null) return input?.trimEnd() ?? ""
    const formatter = new AsYouType(parsedData.country)
    return formatter.input(input)
  } catch {
    return input?.trimEnd() ?? ""
  }
}

export interface IPhoneInputProps {
  label: string
  dark?: boolean
  defaultCountry?: string
  firstCountries?: string[]
  error?: string
  value?: string
  onChange?: (phoneNumber: string) => void
  style?: React.CSSProperties
  className?: string
}
export const PhoneInput = ({ firstCountries = [], defaultCountry, value, onChange, style }: IPhoneInputProps) => {
  const allCountries = useMemo(() => {
    const result = countries.getCountries().reduce<Record<string, CountryPhone>>((acc, country) => {
      Object.assign(acc, {
        [country.iso2 ?? ""]: {
          label: `${country.name} +${country.dialCode}`,
          value: `${country.iso2}`,
          meta: {
            iso2: country.iso2,
            dialCode: country.dialCode,
            priority: country.priority ?? 0,
          },
        },
      })
      return acc
    }, {})
    const firsts = firstCountries.map((first) => result[first.toLowerCase()])
    firstCountries.forEach((first) => delete result[first])
    return [...firsts, ...Object.values(result)]
  }, [firstCountries])

  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [selectedCountry, setSelectedCountry] = useState<CountryPhone | null>(null)
  const [filteredOptions, setFilteredOptions] = useState<IOption<string, ICountryMeta>[]>(allCountries)
  useEffect(() => {
    if (defaultCountry != null) {
      const option = allCountries.find((country) => country.value === defaultCountry.toLowerCase())
      setPhoneNumber(`+${option.meta.dialCode}`)
      setSelectedCountry(option)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry])

  useEffect(() => {
    const val = getFormattedPhone(value)
    if ((val?.length ?? 0) > 0) setPhoneNumber(val)
  }, [value])
  return (
    <Selector
      options={filteredOptions}
      value={selectedCountry}
      style={style}
      onChange={(option) => {
        setSelectedCountry(option)
        setPhoneNumber(`+${option.meta?.dialCode}`)
      }}
      customControl={(props) => (
        <div className="phone-input-control">
          <div className={`iti-flag ${typeof selectedCountry === "string" ? selectedCountry : selectedCountry?.value}`} />
          <input
            {...props}
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value
              const formatted = getFormattedPhone(value)
              setPhoneNumber(formatted)
              onChange?.(value.replace(/ /g, ""))
              const filtered = allCountries.filter((option) => value.startsWith(`+${option.meta.dialCode}`))
              setSelectedCountry(filtered.sort((a, b) => a.meta.priority - b.meta.priority)[0])
              setFilteredOptions(allCountries.filter((country) => country.label.toLowerCase().includes(value.toLowerCase())))
            }}
          />
        </div>
      )}
      renderOptions={(option, props) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }} {...props}>
          <div className={`iti-flag ${option.value}`} />
          {option.label}
        </div>
      )}
    />
  )
}
